import axios from 'axios';

const API_URL = 'http://127.0.0.1:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authService = {
  login: async (email, password) => {
    const response = await api.post('/login', { email, password });
    return response.data;
  },
  
  register: async (name, email, password) => {
    const response = await api.post('/register', {
      name,
      email,
      password,
      password_confirmation: password,
    });
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/logout');
    return response.data;
  },
  
  getUser: async () => {
    const response = await api.get('/me');
    return response.data.user || response.data;
  },
};

export const productService = {
  index: async (params = {}) => {
    const response = await api.get('/products', { params });
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/products', data);
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/products/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },

  storeVariant: async (productId, data) => {
    const response = await api.post(`/products/${productId}/variants`, data);
    return response.data;
  },

  storeOffer: async (productId, data) => {
    const response = await api.post(`/products/${productId}/offers`, data);
    return response.data;
  },

  storeReview: async (productId, data) => {
    const response = await api.post(`/products/${productId}/reviews`, data);
    return response.data;
  },

  storeTag: async (name) => {
    const response = await api.post('/product-tags', { name });
    return response.data;
  },

  attachTags: async (productId, tagIds) => {
    const response = await api.post(`/products/${productId}/tags`, { tag_ids: tagIds });
    return response.data;
  },

  approveReview: async (id) => {
    const response = await api.post(`/product-reviews/${id}/approve`);
    return response.data;
  },
};

export const categoryService = {
  index: async () => {
    const response = await api.get('/categories');
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/categories', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/categories/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const pageService = {
  index: async (params = {}) => {
    const response = await api.get('/pages', { params });
    return response.data;
  },
  
  show: async (slug) => {
    const response = await api.get(`/pages/${slug}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/pages', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/pages/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/pages/${id}`);
    return response.data;
  },
};

export const bannerService = {
  index: async (params = {}) => {
    const response = await api.get('/banners', { params });
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/banners', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/banners/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/banners/${id}`);
    return response.data;
  },
};

export const menuService = {
  index: async (params = {}) => {
    const response = await api.get('/menus', { params });
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/menus', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/menus/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/menus/${id}`);
    return response.data;
  },
  
  addItem: async (menuId, data) => {
    const response = await api.post(`/menus/${menuId}/items`, data);
    return response.data;
  },
};

export const orderService = {
  index: async (params = {}) => {
    const response = await api.get('/orders', { params });
    return response.data;
  },
  
  show: async (orderNumber) => {
    const response = await api.get(`/orders/${orderNumber}`);
    return response.data;
  },
  
  checkout: async (data) => {
    const response = await api.post('/checkout', data);
    return response.data;
  },
  
  updateStatus: async (id, status) => {
    const response = await api.put(`/orders/${id}/status`, { status });
    return response.data;
  },
};

export const cartService = {
  index: async () => {
    const response = await api.get('/cart');
    return response.data;
  },
  
  addItem: async (productId, quantity = 1) => {
    const response = await api.post('/cart/items', {
      product_id: productId,
      quantity,
    });
    return response.data;
  },
  
  updateItem: async (id, quantity) => {
    const response = await api.put(`/cart/items/${id}`, { quantity });
    return response.data;
  },
  
  removeItem: async (id) => {
    const response = await api.delete(`/cart/items/${id}`);
    return response.data;
  },
  
  clear: async () => {
    const response = await api.delete('/cart');
    return response.data;
  },
};

export const addressService = {
  index: async () => {
    const response = await api.get('/addresses');
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/addresses', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/addresses/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/addresses/${id}`);
    return response.data;
  },
};

export const wishlistService = {
  index: async () => {
    const response = await api.get('/wishlist');
    return response.data;
  },
  
  store: async (productId) => {
    const response = await api.post('/wishlist', { product_id: productId });
    return response.data;
  },
  
  destroy: async (productId) => {
    const response = await api.delete(`/wishlist/${productId}`);
    return response.data;
  },
};

export const couponService = {
  index: async () => {
    const response = await api.get('/coupons');
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/coupons', data);
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/coupons/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/coupons/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/coupons/${id}`);
    return response.data;
  },
  
  validate: async (code, orderAmount) => {
    const response = await api.post('/coupons/validate', {
      code,
      order_amount: orderAmount,
    });
    return response.data;
  },
};

export const invoiceService = {
  index: async () => {
    const response = await api.get('/invoices');
    return response.data;
  },
  
  show: async (invoiceNumber) => {
    const response = await api.get(`/invoices/${invoiceNumber}`);
    return response.data;
  },
};

export const inventoryService = {
  index: async (params = {}) => {
    const response = await api.get('/inventory', { params });
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/inventory/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/inventory', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/inventory/${id}`);
    return response.data;
  },
  
  restock: async (id, quantity) => {
    const response = await api.post(`/inventory/${id}/restock`, { quantity });
    return response.data;
  },
};

export const dealerService = {
  index: async () => {
    const response = await api.get('/dealers');
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/dealers/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/dealers', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/dealers/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/dealers/${id}`);
    return response.data;
  },
};

export const distributorService = {
  index: async () => {
    const response = await api.get('/distributors');
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/distributors/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/distributors', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/distributors/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/distributors/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/distributors/${id}/approve`);
    return response.data;
  },
  
  suspend: async (id) => {
    const response = await api.post(`/distributors/${id}/suspend`);
    return response.data;
  },
};

export const investmentService = {
  index: async () => {
    const response = await api.get('/investments');
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/investments/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/investments', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/investments/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/investments/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/investments/${id}/approve`);
    return response.data;
  },
  
  withdraw: async (id) => {
    const response = await api.post(`/investments/${id}/withdraw`);
    return response.data;
  },
};

export const commissionService = {
  index: async () => {
    const response = await api.get('/commissions');
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/commissions/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/commissions', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/commissions/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/commissions/${id}`);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await api.post(`/commissions/${id}/approve`);
    return response.data;
  },
  
  reject: async (id) => {
    const response = await api.post(`/commissions/${id}/reject`);
    return response.data;
  },
  
  pay: async (id) => {
    const response = await api.post(`/commissions/${id}/pay`);
    return response.data;
  },
};

export const blogService = {
  index: async (params = {}) => {
    const response = await api.get('/blogs', { params });
    return response.data;
  },
  
  show: async (slug) => {
    const response = await api.get(`/blogs/${slug}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/blogs', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/blogs/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/blogs/${id}`);
    return response.data;
  },
};

export const jobService = {
  index: async (params = {}) => {
    const response = await api.get('/jobs', { params });
    return response.data;
  },
  
  show: async (id) => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },
  
  store: async (data) => {
    const response = await api.post('/jobs', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await api.put(`/jobs/${id}`, data);
    return response.data;
  },
  
  destroy: async (id) => {
    const response = await api.delete(`/jobs/${id}`);
    return response.data;
  },
  
  applications: async (id) => {
    const response = await api.get(`/jobs/${id}/applications`);
    return response.data;
  },
  
  apply: async (id, data) => {
    const response = await api.post(`/jobs/${id}/apply`, data);
    return response.data;
  },
};

export const reportService = {
  dashboard: async () => {
    const response = await api.get('/reports/dashboard');
    return response.data;
  },
  
  sales: async (params = {}) => {
    const response = await api.get('/reports/sales', { params });
    return response.data;
  },
  
  topProducts: async (params = {}) => {
    const response = await api.get('/reports/top-products', { params });
    return response.data;
  },
  
  inventory: async () => {
    const response = await api.get('/reports/inventory');
    return response.data;
  },
  
  customers: async () => {
    const response = await api.get('/reports/customers');
    return response.data;
  },
};

export const newsletterService = {
  subscribe: async (data) => {
    const response = await api.post('/newsletter/subscribe', data);
    return response.data;
  },
};

export const notificationService = {
  index: async () => {
    const response = await api.get('/notifications');
    return response.data;
  },
  store: async (data) => {
    const response = await api.post('/notifications', data);
    return response.data;
  },
  markAsRead: async (id) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
};

export const adService = {
  index: async () => {
    const response = await api.get('/ads');
    return response.data;
  },
  store: async (data) => {
    const response = await api.post('/ads', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/ads/${id}`, data);
    return response.data;
  },
  destroy: async (id) => {
    const response = await api.delete(`/ads/${id}`);
    return response.data;
  },
};

export default api;
